import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";

// Lazy load pages for better code splitting
const Index = lazy(() => import("./pages/Index"));


const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const About = lazy(() => import("./pages/About"));
const GuidaPropagazione = lazy(() => import("./pages/GuidaPropagazione"));
const QuizRadioamatore = lazy(() => import("./pages/QuizRadioamatore"));
const TroubleshootingRadio = lazy(() => import("./pages/TroubleshootingRadio"));
const GlossarioDX = lazy(() => import("./pages/GlossarioDX"));
const PrefissiInternazionali = lazy(() => import("./pages/PrefissiInternazionali"));


// Optimized QueryClient with better caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 4, // 4 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

// Animated loading fallback with radar theme
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#171717' }}>
    <div className="flex flex-col items-center gap-6">
      {/* Radar animation */}
      <div className="relative w-24 h-24">
        {/* Outer ring */}
        <div 
          className="absolute inset-0 rounded-full border-2 opacity-30"
          style={{ borderColor: '#f97316' }}
        />
        {/* Middle ring */}
        <div 
          className="absolute inset-3 rounded-full border opacity-20"
          style={{ borderColor: '#f97316' }}
        />
        {/* Inner ring */}
        <div 
          className="absolute inset-6 rounded-full border opacity-15"
          style={{ borderColor: '#f97316' }}
        />
        {/* Center dot */}
        <div 
          className="absolute inset-[42%] rounded-full animate-pulse"
          style={{ backgroundColor: '#f97316' }}
        />
        {/* Radar sweep */}
        <div 
          className="absolute inset-0 rounded-full origin-center"
          style={{ 
            animation: 'spin 2s linear infinite',
            background: 'conic-gradient(from 0deg, transparent 0deg, rgba(249, 115, 22, 0.4) 30deg, transparent 60deg)'
          }}
        />
        {/* Grid lines */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full h-[1px]" style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)' }} />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[1px] h-full" style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)' }} />
        </div>
      </div>
      
      {/* Text */}
      <div className="flex flex-col items-center gap-2">
        <div className="flex items-center gap-2">
          <div 
            className="w-2 h-2 rounded-full animate-pulse" 
            style={{ backgroundColor: '#f97316' }} 
          />
          <span 
            className="text-sm font-mono tracking-wider"
            style={{ color: '#f97316' }}
          >
            CB DX MONITOR
          </span>
        </div>
        <div className="flex gap-1">
          <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: '#f97316', animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: '#f97316', animationDelay: '150ms' }} />
          <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: '#f97316', animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false} disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={300}>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                
                
                <Route path="/guida-propagazione-cb" element={<GuidaPropagazione />} />
                <Route path="/troubleshooting-radio" element={<TroubleshootingRadio />} />
                <Route path="/glossario-dx" element={<GlossarioDX />} />
                <Route path="/prefissi-internazionali" element={<PrefissiInternazionali />} />
                
                <Route path="/quiz-radioamatore" element={<QuizRadioamatore />} />
                <Route path="/about" element={<About />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
